import { Controller, useFormContext } from 'react-hook-form';
import { Grid, InputLabel, Radio, FormControlLabel, FormControl, FormLabel, RadioGroup, Select, MenuItem, FormHelperText } from '@mui/material';
import type { CountryEntity } from '@shared/api';
import { Nationalities } from './Nationalities';
import { CustomDatePicker } from './CustomDatePicker';
import { ValidateUtils } from '@shared/utils';
import { Input } from '@shared/widgets';

const MARITAL_STATUS_RADIOS = [
  { label: 'Married', value: 'Married' },
  { label: 'Single', value: 'Single' },
  { label: 'Other', value: 'Other' },
];

interface Props {
  countries: CountryEntity[];
  nationalities: string[];
  readOnly?: boolean;
}

export const StepApplicant = (props: Props) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const nationality = watch('nationality');

  return (
    <>
      <Grid container>
        <Grid item xs={2}>
          <Controller
            name='title'
            control={control}
            rules={{ required: 'Title is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label='Title'
                margin='normal'
                required
                error={!!error}
                helperText={error?.message}
                inputProps={{ readOnly: props.readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <Controller
            name='firstName'
            control={control}
            rules={{ required: 'First Name is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label='First Name'
                margin='normal'
                required
                error={!!error}
                helperText={error?.message}
                inputProps={{ readOnly: props.readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item xs={2}>
          <Controller
            name='middleName'
            control={control}
            render={({ field }) => <Input {...field} label='Middle Name' margin='normal' inputProps={{ readOnly: props.readOnly }} />}
          />
        </Grid>
        <Grid item xs={4}>
          <Controller
            name='lastName'
            control={control}
            rules={{ required: 'Surname is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label='Surname'
                margin='normal'
                required
                error={!!error}
                helperText={error?.message}
                inputProps={{ readOnly: props.readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='nameOnCard'
            control={control}
            rules={{ required: 'Name on Card is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label='Name on Card'
                margin='normal'
                required
                error={!!error}
                helperText={error?.message || '(Maximum 21 letters including spaces. Initials for the first name are acceptable, symbols are not)'}
                inputProps={{ readOnly: props.readOnly, maxLength: 21 }}
              />
            )}
          />
        </Grid>
        {nationality && (
          <Grid item xs={3}>
            {props.nationalities.length > 0 ? (
              <FormControl margin='normal' fullWidth>
                <InputLabel>Nationality *</InputLabel>
                <Controller
                  name='nationality'
                  control={control}
                  rules={{ required: 'Nationality is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Select {...field} variant='standard' fullWidth error={!!error}>
                      {props.nationalities.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            ) : (
              <Controller
                name='nationality'
                control={control}
                rules={{ required: 'Nationality is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    label='Nationality'
                    margin='normal'
                    required
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ readOnly: props.readOnly }}
                  />
                )}
              />
            )}
          </Grid>
        )}
        <Grid item xs={nationality ? 3 : 6}>
          {props.countries.length > 0 ? (
            <FormControl margin='normal' fullWidth error={!!errors.countryOfBirth}>
              <InputLabel>Country of Birth *</InputLabel>
              <Controller
                name='countryOfBirth'
                control={control}
                rules={{ required: 'Country of Birth is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select {...field} variant='standard' fullWidth error={!!error}>
                      {props.countries.map((item) => (
                        <MenuItem key={item.name} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>{error?.message}</FormHelperText>
                  </>
                )}
              />
            </FormControl>
          ) : (
            <Controller
              name='countryOfBirth'
              control={control}
              rules={{ required: 'Country of Birth is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  label='Country of Birth'
                  margin='normal'
                  required
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{ readOnly: props.readOnly }}
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          <FormControl margin='normal' fullWidth error={!!errors.maritalStatus}>
            <FormLabel component='label'>Marital Status *</FormLabel>
            <RadioGroup row>
              {MARITAL_STATUS_RADIOS.map((radio, idx) => (
                <FormControlLabel
                  key={idx}
                  label={radio.label}
                  labelPlacement='end'
                  control={
                    <Controller
                      name='maritalStatus'
                      control={control}
                      rules={{ required: 'Marital Status is required' }}
                      render={({ field }) => <Radio color='primary' {...field} value={radio.value} checked={radio.value === field.value} />}
                    />
                  }
                />
              ))}
            </RadioGroup>
            {errors?.maritalStatus?.message && <FormHelperText>{errors.maritalStatus.message as string}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <Controller
            name='dob'
            control={control}
            rules={{
              required: 'Date of Birth is required',
              validate: { isValidDate: ValidateUtils.isValidDate, isAdult: ValidateUtils.isAdult },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomDatePicker
                label='Date of Birth'
                margin='normal'
                inputFormat='dd/MM/yyyy'
                fullWidth
                required
                error={!!error}
                helperText={error?.message}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name='passport_IDCardNo'
            control={control}
            rules={{ required: 'Passport / ID Card Number is required' }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                label='Passport / ID Card Number'
                margin='normal'
                required
                error={!!error}
                helperText={error?.message}
                inputProps={{ readOnly: props.readOnly }}
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          {props.countries.length > 0 ? (
            <FormControl margin='normal' fullWidth error={!!errors.countryOfIssuance}>
              <InputLabel>Country of Issuance *</InputLabel>
              <Controller
                name='countryOfIssuance'
                control={control}
                rules={{ required: 'Country of Issuance is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select {...field} variant='standard' fullWidth>
                      {props.countries.map((item, idx) => (
                        <MenuItem key={idx} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>{error?.message}</FormHelperText>
                  </>
                )}
              />
            </FormControl>
          ) : (
            <Controller
              name='countryOfIssuance'
              control={control}
              rules={{ required: 'Country of Issuance is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  label='Country of Issuance'
                  margin='normal'
                  required
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{ readOnly: props.readOnly }}
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={2}>
          <Controller
            name='passportExpiryDate'
            control={control}
            rules={{
              required: 'Expiry Date is required',
              validate: { isValidDate: ValidateUtils.isValidDate, isFutureDate: ValidateUtils.isFutureDate },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomDatePicker
                label='Expiry Date'
                margin='normal'
                inputFormat='dd/MM/yyyy'
                fullWidth
                required
                error={!!error}
                helperText={error?.message}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Grid>
      </Grid>
      <Nationalities nationalities={props.nationalities} />
    </>
  );
};
